import { Link, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Home, LifeBuoy, Search, ArrowLeft } from "lucide-react";
import Seo from "@/components/Seo";
import Footer from "@/components/Footer";
import ProductCard from "@/components/ProductCard";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function NotFoundPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [q, setQ] = useState("");
  const [categories, setCategories] = useState([]);
  const [popular, setPopular] = useState([]);

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCategories((data || []).slice(0, 10))).catch(() => {});
    api.get("/popular-products", { params: { limit: 4 } }).then(({ data }) => setPopular(data || [])).catch(() => {});
  }, []);

  const submit = (e) => {
    e.preventDefault();
    const term = q.trim();
    if (term) navigate(`/search/${encodeURIComponent(term)}`);
  };

  return (
    <div className="min-h-screen flex flex-col bg-white" data-testid="not-found-page">
      <Seo
        title="Page not found (404)"
        description="The page you were looking for has moved or no longer exists. Search our catalogue or head back to the ACP Online homepage."
        noindex
        status404
      />

      <header className="border-b">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <Link to="/" className="text-2xl font-extrabold tracking-tight text-steel" data-testid="nf-logo">
            ACP<span className="text-accent">Online</span>
          </Link>
        </div>
      </header>

      <main className="flex-1">
        <div className="max-w-3xl mx-auto px-4 py-16 text-center">
          <p className="text-7xl sm:text-8xl font-black text-slate-200 select-none">404</p>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-steel mt-2">This page has moved or no longer exists</h1>
          <p className="text-muted-foreground mt-3 text-base">
            The link you followed may be from our previous website. Try searching our catalogue,
            or use one of the quick links below.
          </p>
          <p className="text-xs text-slate-400 mt-2 break-all" data-testid="nf-path">Requested: {location.pathname}</p>

          <form onSubmit={submit} className="mt-8 flex gap-2 max-w-xl mx-auto" data-testid="nf-search-form">
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search products, brands or SKUs…"
              data-testid="nf-search-input"
              className="h-12"
            />
            <Button type="submit" data-testid="nf-search-btn" className="h-12 bg-accent text-steel hover:bg-amber-600 font-semibold px-5">
              <Search size={18} className="mr-1.5" /> Search
            </Button>
          </form>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Button asChild variant="default" className="bg-steel text-white hover:bg-steel-light font-semibold" data-testid="nf-home-btn">
              <Link to="/"><Home size={16} className="mr-1.5" /> Back to home</Link>
            </Button>
            <Button asChild variant="outline" data-testid="nf-help-btn">
              <Link to="/help"><LifeBuoy size={16} className="mr-1.5" /> Help &amp; support</Link>
            </Button>
            <Button asChild variant="outline" data-testid="nf-contact-btn">
              <Link to="/help/contact">Contact us</Link>
            </Button>
            <button onClick={() => navigate(-1)} className="inline-flex items-center text-sm text-muted-foreground hover:text-steel" data-testid="nf-back-btn">
              <ArrowLeft size={15} className="mr-1" /> Go back
            </button>
          </div>
        </div>

        {categories.length > 0 && (
          <div className="max-w-5xl mx-auto px-4 pb-4" data-testid="nf-categories">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground text-center mb-4">Popular categories</h2>
            <div className="flex flex-wrap justify-center gap-2.5">
              {categories.map((c) => (
                <Link
                  key={c.id}
                  to={`/category/${c.slug}`}
                  data-testid={`nf-category-${c.slug}`}
                  className="px-4 py-2 rounded-full border bg-white text-sm font-medium text-steel hover:border-accent hover:text-accent transition-colors"
                >
                  {c.name}
                </Link>
              ))}
            </div>
          </div>
        )}

        {popular.length > 0 && (
          <div className="max-w-6xl mx-auto px-4 py-12" data-testid="nf-popular">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground text-center mb-6">Popular right now</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
              {popular.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
